/** At app start, fetch the kept views that the role can see, 2 at a time in idle time (design 16.8). */

import { currentScope, note, whenOpen } from "./store";
import { sizeOf } from "./viewRunner";
import { fetchFailed, fetching, isStale, keepView, keptView, lastFetch, minRefetchMs, type ViewDef, viewDefs } from "./views";

export const WARM_CONCURRENCY = 2;

/** The browser seams; the tests change them. */
export const warmDeps = {
  idle: (run: () => void): void => {
    if (typeof requestIdleCallback === "function") requestIdleCallback(run, { timeout: 5_000 });
    else setTimeout(run, 200);
  },
  now: () => Date.now(),
};

/** A driver keeps the Today and Visit data of the offline day; the warm-up fetches nothing for them. */
export const onlyDriver = (roles: readonly string[]) => roles.length > 0 && roles.every((role) => role === "driver");

/** The views to warm: each defined view whose module the role can view, and that was not fetched on this page load. */
export function warmList(views: readonly string[], roles: readonly string[], defs: readonly ViewDef[] = viewDefs()): ViewDef[] {
  if (onlyDriver(roles)) return [];
  return defs.filter((def) => def.module !== null && def.warm && views.includes(def.module) && lastFetch(def.warm.key) === null);
}

const errorName = (e: unknown) => (e && typeof e === "object" && "status" in e ? `http_${String((e as { status: unknown }).status)}` : e instanceof Error ? e.name : "unknown");

/** True when the kept view on disk is younger than the least refetch time of its def, and not stale; it then counts as fetched. */
function freshOnDisk(key: string, now: number): boolean {
  const kept = keptView(key);
  if (!kept || isStale(key) || now - kept.at >= minRefetchMs(key)) return false;
  fetching(key, kept.at);
  return true;
}

/** Start the warm-up; the result resolves when all fetches end, and `cancel` stops it and drops the answers still in flight. */
export function warmViews(views: readonly string[], roles: readonly string[], token: () => Promise<string>): { done: Promise<void>; cancel: () => void } {
  let cancelled = false;
  if (onlyDriver(roles)) note("view_warm_skip", "warm-up: none for a driver", { reason: "driver" });
  const scope = currentScope();
  const done = whenOpen().then(() => (cancelled ? undefined : run(views, roles, token, scope, () => cancelled)));
  return {
    done,
    cancel: () => {
      cancelled = true;
    },
  };
}

async function run(views: readonly string[], roles: readonly string[], token: () => Promise<string>, scope: string | null, cancelled: () => boolean): Promise<void> {
  if (currentScope() !== scope) {
    note("view_warm_dropped", "warm-up: the scope changed before the start", { reason: "scope_change" });
    return;
  }
  const listed = warmList(views, roles);
  const now = warmDeps.now();
  const queue = listed.filter((def) => !freshOnDisk(def.warm!.key, now));
  const skipped = listed.length - queue.length;
  if (skipped) note("view_warm_fresh", `warm-up: ${skipped} views fresh on disk, not fetched`, { views: skipped });
  if (!queue.length) return;
  const started = performance.now();
  const total = queue.length;
  let fetched = 0;
  let failed = 0;
  let dropped = 0;
  note("view_warm_start", `warm-up: ${total} views, ${WARM_CONCURRENCY} at a time, ${skipped} fresh`, { views: total, skipped });

  const gone = (def: ViewDef): boolean => {
    const reason = cancelled() ? "cancelled" : currentScope() !== scope ? "scope_change" : null;
    if (!reason) return false;
    dropped += 1;
    note("view_warm_dropped", `warm-up: ${def.name} answer dropped (${reason})`, { view: def.name, reason });
    return true;
  };

  const one = async (def: ViewDef) => {
    const warm = def.warm!;
    if (lastFetch(warm.key) !== null) return;
    const at = warmDeps.now();
    const begun = performance.now();
    fetching(warm.key, at);
    try {
      const data = await warm.load(await token());
      if (gone(def)) return;
      keepView(warm.key, data, at);
      fetched += 1;
      const ms = Math.round(performance.now() - begun);
      const bytes = sizeOf(data);
      note("view_warm", `warm-up: ${def.name} in ${ms} ms, ${bytes} bytes`, { view: def.name, ms, bytes });
    } catch (e) {
      if (gone(def)) return;
      failed += 1;
      fetchFailed(warm.key);
      note("view_warm_failed", `warm-up: ${def.name} failed: ${errorName(e)}`, { view: def.name, error: errorName(e) });
    }
  };
  const worker = async () => {
    for (;;) {
      if (cancelled() || currentScope() !== scope) return;
      const def = queue.shift();
      if (!def) return;
      await new Promise<void>((resolve) => warmDeps.idle(resolve));
      if (cancelled() || currentScope() !== scope) return;
      await one(def);
    }
  };
  await Promise.all(Array.from({ length: Math.min(WARM_CONCURRENCY, total) }, worker));
  const ms = Math.round(performance.now() - started);
  note("view_warm_done", `warm-up: ${fetched} of ${total} views in ${ms} ms, ${skipped} fresh, ${failed} failed, ${dropped} dropped`, { views: total, fetched, skipped, failed, dropped, ms });
}
